// In-app guide for the sales team. Opened from the topbar ⋯ → Guide.
// Two card sections: deploying a sample (with the two walkthrough images) and
// auto-approval. Concise, mobile-first, solid readable background.

import { useEffect } from "react";
import { X } from "react-feather";
import { useSession } from "../store/session";

export default function Guide() {
  const closeGuide = useSession((s) => s.closeGuide);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeGuide();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeGuide]);

  return (
    <div className="guide" role="dialog" aria-modal="true" aria-label="Guide">
      <div className="guide__header">
        <h1 className="guide__title brand-font">
          <span className="text-gradient">Reputifly Agent</span> Guide
        </h1>
        <button type="button" className="guide__close" onClick={closeGuide} aria-label="Close guide">
          <X width={18} height={18} />
        </button>
      </div>

      <div className="guide__scroll">
        {/* ======================== DEPLOY A SAMPLE ======================== */}
        <section className="guide__card">
          <h2 className="guide__h2">Deploy a sample for a lead</h2>
          <p className="guide__lede">
            Turn a lead's WhatsApp chat into a free, live demo site — built from their real
            business — that you can send them in minutes.
          </p>

          <h3 className="guide__h3">1 · Start a new sample</h3>
          <p>Open the <strong>Samples</strong> tab and tap <strong>+</strong>.</p>
          <img className="guide__img" src="/guide-sample-1.jpg" alt="Open the Samples tab, then tap the + button" loading="lazy" />

          <h3 className="guide__h3">2 · Add the chat, your email, then send</h3>
          <ul className="guide__list">
            <li>Tap <strong>+</strong> and attach the lead's WhatsApp chat export (the .zip).</li>
            <li>Type <code>/email your@address</code> — it emails you the live link when done.</li>
            <li>Hit send. It builds, deploys, and checks its own work.</li>
          </ul>
          <img className="guide__img" src="/guide-sample-2.jpg" alt="Add the chat export, type /email your email, then send" loading="lazy" />

          <h3 className="guide__h3">3 · Send the lead the two messages</h3>
          <p>When it's done, the chat shows a <strong>“What to say”</strong> card — tap Copy on each and send both, one after the other:</p>
          <div className="guide__say">
            <p>made your site, it's live 👉 <code>link</code></p>
            <p>what do you think? anything you want to change, i'll sort it out</p>
          </div>

          <h3 className="guide__h3">Tips</h3>
          <ul className="guide__list">
            <li>Always attach the <strong>real</strong> chat export — that's what makes it feel like theirs.</li>
            <li>Set the sample's thinking level in <strong>⋯ → Global settings</strong>. Max = best, priciest.</li>
          </ul>
        </section>

        {/* ========================= AUTO-APPROVAL ========================= */}
        <section className="guide__card">
          <h2 className="guide__h2">Auto-approval</h2>
          <p className="guide__lede">
            Lets a paying client's <strong>in-scope</strong> revision requests be handled
            automatically — no click from you. Off by default, set per client.
          </p>

          <h3 className="guide__h3">How it works</h3>
          <ul className="guide__list">
            <li>Turn it on per client: open the client, then <strong>⋯ → Auto-approve</strong>.</li>
            <li>Anything <strong>new or out-of-scope still waits for you</strong> — it posts a plan and stops.</li>
            <li>Every change is backed up first, so it's reversible.</li>
          </ul>

          <h3 className="guide__h3">Safety</h3>
          <div className="guide__warn">
            <ul className="guide__list">
              <li>Only turn it on for clients you're happy to let run unattended.</li>
              <li>It only ever touches <strong>that one client's own site</strong> — never the whole fleet.</li>
              <li>Keep it off for anything high-stakes or mid-migration.</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
